import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Grid,
  Paper,
  Avatar,
  Chip,
  Rating,
  CircularProgress,
  Divider,
  Button,
  lighten,
} from "@mui/material";
import {
  ClearOutlined,
  SearchOutlined,
  TipsAndUpdatesOutlined,
  BusinessOutlined,
  PersonOutlineOutlined,
  AccessTimeOutlined,
  InsertDriveFileOutlined,
  DownloadOutlined,
  InfoOutlined,
  PeopleAltOutlined,
  Star,
  DescriptionOutlined,
  BugReportOutlined,
  AutoAwesomeOutlined,
  PaletteOutlined,
  SpeedOutlined,
  MenuBookOutlined,
  SecurityOutlined,
  CategoryOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/system";
import { useTheme } from "@emotion/react";
import CardAllAnswers from "../cardAllAnswers";
import MetricCard from "../metricCard";
import StarRatingChart from "../StartRatingChart";
import CommentsSection from "../commentsSections/commentsSections";
import SelectionChart from "../selectionChart";

const ModernCategoryChip = ({ answer, theme }) => {
  const answerKey = String(answer || "").toLowerCase().trim();

  let label = answer || "Nenhuma categoria selecionada";
  let IconComponent = CategoryOutlined;
  let bgcolor = alpha(theme.palette.text.secondary, 0.12);
  let color = theme.palette.text.primary;
  let borderColor = alpha(theme.palette.divider, 0.8);

  if (answerKey === "bug") {
    label = "Bug";
    IconComponent = BugReportOutlined;
    bgcolor = alpha(theme.palette.error.main, 0.12);
    color = theme.palette.mode === "dark" ? theme.palette.error.light : theme.palette.error.dark;
    borderColor = alpha(theme.palette.error.main, 0.35);
  } else if (answerKey === "melhoria") {
    label = "Melhoria";
    IconComponent = AutoAwesomeOutlined;
    bgcolor = alpha(theme.palette.primary.main, 0.12);
    color = theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.dark;
    borderColor = alpha(theme.palette.primary.main, 0.35);
  } else if (answerKey === "ux") {
    label = "Interface / UX";
    IconComponent = PaletteOutlined;
    bgcolor = alpha(theme.palette.secondary.main, 0.12);
    color = theme.palette.mode === "dark" ? theme.palette.secondary.light : theme.palette.secondary.dark;
    borderColor = alpha(theme.palette.secondary.main, 0.35);
  } else if (answerKey === "performance") {
    label = "Performance";
    IconComponent = SpeedOutlined;
    bgcolor = alpha(theme.palette.warning.main, 0.14);
    color = theme.palette.mode === "dark" ? theme.palette.warning.light : theme.palette.warning.dark;
    borderColor = alpha(theme.palette.warning.main, 0.35);
  } else if (answerKey === "documentacao") {
    label = "Documentação";
    IconComponent = MenuBookOutlined;
    bgcolor = alpha(theme.palette.info.main, 0.12);
    color = theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.dark;
    borderColor = alpha(theme.palette.info.main, 0.35);
  } else if (answerKey === "seguranca") {
    label = "Segurança";
    IconComponent = SecurityOutlined;
    bgcolor = alpha(theme.palette.error.dark, 0.16);
    color = theme.palette.mode === "dark" ? theme.palette.error.light : theme.palette.error.dark;
    borderColor = alpha(theme.palette.error.dark, 0.4);
  } else if (answerKey === "outro") {
    label = "Outro";
    IconComponent = CategoryOutlined;
  }

  return (
    <Chip
      icon={
        <IconComponent
          sx={{
            fontSize: "16px !important",
            color: `${color} !important`,
            ml: "6px !important",
          }}
        />
      }
      label={label}
      size="small"
      sx={{
        bgcolor,
        color,
        border: `1px solid ${borderColor}`,
        borderRadius: "8px",
        fontWeight: 600,
        px: 0.5,
        height: 26,
        boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
          bgcolor: alpha(bgcolor, 0.85),
        },
      }}
    />
  );
};

const ModernAttachmentCard = ({ anx, downloadAnexo, theme }) => {
  const ext =
    anx?.nome?.split(".")?.pop()?.toUpperCase() ||
    anx?.mimeType?.split("/")?.pop()?.toUpperCase() ||
    "ARQUIVO";

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: 1.5,
        pr: 1.8,
        borderRadius: "12px",
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.05)
            : alpha(theme.palette.primary.main, 0.03),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.02)",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        minWidth: { xs: "100%", sm: "280px" },
        maxWidth: "360px",
        flex: "1 1 280px",
        
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, overflow: "hidden" }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "10px",
            bgcolor: alpha(theme.palette.primary.main, 0.14),
            color: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <InsertDriveFileOutlined sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <Typography
            variant="body2"
            title={anx.nome}
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {anx.nome || "Anexo sem nome"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.3 }}>
            <Chip
              label={ext}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                borderRadius: "4px",
              }}
            />
          </Box>
        </Box>
      </Box>

      <Button
        size="small"
        variant="contained"
        color="primary"
        startIcon={<DownloadOutlined sx={{ fontSize: "16px !important" }} />}
        onClick={() => downloadAnexo(anx)}
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
          px: 1.8,
          py: 0.5,
          boxShadow: "none",
          flexShrink: 0,
          "&:hover": {
            boxShadow: theme.shadows[2],
          },
        }}
      >
        Baixar
      </Button>
    </Paper>
  );
};

const ModernSubmissionHeader = ({ ans, theme }) => {
  const formattedDate = new Date(ans.createAt).toLocaleString("pt-BR", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        pb: 2.2,
        mb: 2.2,
        borderBottom: `1px dashed ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <BusinessOutlined sx={{ color: theme.palette.primary.main, fontSize: 26 }} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              fontSize: "1.08rem",
              color: theme.palette.text.primary,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            {ans.clientName || "Empresa / Cliente"}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Chip
              icon={
                <PersonOutlineOutlined
                  sx={{
                    fontSize: "15px !important",
                    color: "inherit !important",
                    ml: "6px !important",
                  }}
                />
              }
              label={ans.userLogin || "Usuário não informado"}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.75rem",
                fontWeight: 600,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.info.main, 0.15)
                    : alpha(theme.palette.info.main, 0.08),
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.info.light
                    : theme.palette.info.dark,
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                borderRadius: "8px",
              }}
            />
          </Box>
        </Box>
      </Box>

      <Chip
        icon={
          <AccessTimeOutlined
            sx={{
              fontSize: "15px !important",
              color: "inherit !important",
              ml: "6px !important",
            }}
          />
        }
        label={formattedDate}
        size="small"
        sx={{
          height: 26,
          fontSize: "0.75rem",
          fontWeight: 600,
          bgcolor: alpha(theme.palette.text.secondary, 0.08),
          color: theme.palette.text.secondary,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          borderRadius: "8px",
        }}
      />
    </Box>
  );
};

export default function SuggestionDetailsModal({
  open,
  handleClose,
  data,
  form,
  loading,
}) {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [search, setSearch] = useState("");

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const downloadAnexo = (anexo) => {
    if (!anexo?.dadosBase64) return;
    const link = document.createElement("a");
    link.href = `data:${anexo.mimeType || "application/octet-stream"};base64,${anexo.dadosBase64}`;
    link.download = anexo.nome || "anexo";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const individualAnswers = data?.individualAnswers || [];
  const filteredAnswers = individualAnswers.filter((ans) => {
    if (!search.trim()) return true;
    const query = search.toLowerCase();
    const matchClient = ans?.clientName?.toLowerCase().includes(query);
    const matchUser = ans?.userLogin?.toLowerCase().includes(query);
    const matchResponses = ans?.questionResponses?.some((qr) =>
      String(qr?.answer || "")
        .toLowerCase()
        .includes(query)
    );
    return matchClient || matchUser || matchResponses;
  });

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: "75%", lg: "65%", xl: "58%" },
          maxWidth: "1150px",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: theme.shadows[10],
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.background.default
                : "#f8f9fa",
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 48,
                height: 48,
              }}
            >
              <TipsAndUpdatesOutlined />
            </Avatar> */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {form?.title || "Detalhes das Sugestões de Melhoria"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Acompanhe o feedback, sugestões e anexos enviados pelos clientes.
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
           
          >
            <ClearOutlined />
          </IconButton>
        </Box>

        {loading ? (
          <Box
            sx={{
              height: 400,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
            }}
          >
            {/* Cards de Métricas Rápidas */}
            <Box sx={{ p: 3, pb: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <CardAllAnswers
                    colorCard={theme.palette.cardTrafficData.paperColor.statusUp}
                    icon={PeopleAltOutlined}
                    colorIcon={theme.palette.colors.green.light}
                    title="Submissões Recebidas"
                    descricao="Total de sugestões enviadas"
                    value={
                      data?.analiseAnswers?.totalRespostas ||
                      individualAnswers.length
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CardAllAnswers
                    colorCard={theme.palette.cardTrafficData.paperColor.inputData}
                    icon={BusinessOutlined}
                    colorIcon={theme.palette.colors.blue.light}
                    title="Clientes Participantes"
                    descricao="Empresas que enviaram sugestões"
                    value={
                      new Set(individualAnswers.map((ans) => ans?.clientName)).size
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CardAllAnswers
                    colorCard={theme.palette.cardTrafficData.paperColor.outputData}
                    icon={DescriptionOutlined}
                    colorIcon={lighten(theme.palette.cardTrafficData.paperColor.outputData, 0.32)}
                    title="Arquivos Anexados"
                    descricao="Anexos e prints recebidos"
                    value={
                      individualAnswers.reduce(
                        (acc, ans) => acc + (ans?.anexos?.length || 0),
                        0
                      )
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Tabs */}
            <Box sx={{ px: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab
                  label={`Respostas Individuais dos Clientes (${individualAnswers.length})`}
                  sx={{ fontWeight: 600 }}
                />
                <Tab label="Resumo Agregado (Gráficos)" sx={{ fontWeight: 600 }} />
              </Tabs>
            </Box>

            {/* Tab 0: Respostas Individuais */}
            {tabIndex === 0 && (
              <Box
                sx={{
                  p: 3,
                  flex: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {/* Filtro de busca */}
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Pesquisar por cliente, usuário ou texto da resposta..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchOutlined color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      maxWidth: 450,
                      bgcolor: "background.paper",
                    }}
                  />
                </Box>

                {filteredAnswers.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 8,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "background.default"
                          : "#f8f9fa",
                      borderRadius: "12px",
                      border: `1px dashed ${theme.palette.divider}`,
                    }}
                  >
                    <InfoOutlined
                      sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="h6" color="textSecondary">
                      Nenhuma resposta de cliente encontrada.
                    </Typography>
                  </Box>
                ) : (
                  filteredAnswers.map((ans) => (
                    <Paper
                      key={ans.id}
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.shadows[1],
                        transition: "all 0.2s",
                       
                      }}
                    >
                      {/* Header da Submissão */}
                      <ModernSubmissionHeader ans={ans} theme={theme} />

                      {/* Respostas por Pergunta */}
                      <Grid container spacing={2.5}>
                        {ans.questionResponses?.map((qr, idx) => (
                          <Grid item xs={12} key={idx}>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  mb: 0.8,
                                }}
                              >
                                {idx + 1}. {qr.question}
                              </Typography>

                              {qr.type === 0 ? (
                                !isNaN(Number(qr.answer)) &&
                                Number(qr.answer) >= 1 &&
                                Number(qr.answer) <= 5 &&
                                String(qr.answer).trim() !== "" ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Rating value={Number(qr.answer) || 0} readOnly />
                                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                                      ({qr.answer || 0} de 5 estrelas)
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                                    <ModernCategoryChip answer={qr.answer} theme={theme} />
                                  </Box>
                                )
                              ) : qr.type === 1 ? (
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    bgcolor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(255, 255, 255, 0.03)"
                                        : "#f8f9fa",
                                    borderRadius: "8px",
                                    border: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                    {qr.answer || "Sem resposta preenchida"}
                                  </Typography>
                                </Paper>
                              ) : (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                                  {qr.answer ? (
                                    qr.answer.split(", ").map((optTxt, optIdx) => (
                                      <Chip
                                        key={optIdx}
                                        label={optTxt}
                                        size="small"
                                        sx={{
                                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                                          color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.dark,
                                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                          borderRadius: "8px",
                                          fontWeight: 600,
                                          px: 0.3,
                                          height: 26,
                                          boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
                                        }}
                                      />
                                    ))
                                  ) : (
                                    <Chip
                                      label="Nenhuma opção selecionada"
                                      color="default"
                                      variant="outlined"
                                      size="small"
                                      sx={{ borderRadius: "8px" }}
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      {/* Anexos */}
                      {ans.anexos?.length > 0 && (
                        <Box
                          sx={{
                            mt: 3.5,
                            pt: 2.5,
                            borderTop: `1px dashed ${theme.palette.divider}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                              mb: 2,
                              justifyContent: "space-between",
                            }}
                          >
                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "6px",
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.main,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <DescriptionOutlined sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Arquivos Anexados nesta resposta
                            </Typography>
                            </Box>
                            <Chip
                              label={`${ans.anexos.length} anexo(s)`}
                              size="small"
                              color="primary"
                              sx={{
                                height: 22,
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                borderRadius: "6px",
                              }}
                            />
                          </Box>

                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.8 }}>
                            {ans.anexos.map((anx) => (
                              <ModernAttachmentCard
                                key={anx.id}
                                anx={anx}
                                downloadAnexo={downloadAnexo}
                                theme={theme}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
            )}

            {/* Tab 1: Resumo Agregado */}
            {tabIndex === 1 && (
              <Box
                sx={{
                  p: 3,
                  flex: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {data?.dataAanaliseForPosition?.map((question, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ mb: 3, fontWeight: 600, textAlign: "justify" }}
                    >
                      {index + 1} - {question?.question}
                    </Typography>
                    <Grid container spacing={3} alignItems="center">
                      {(question?.type === 0 || question?.type === 2) && (
                        <Box sx={{ width: "100%", px: 2 }}>
                          <SelectionChart data={question?.dataAnalise || []} />
                        </Box>
                      )}
                      {question?.type === 1 && (
                        <Box sx={{ width: "100%", px: 2 }}>
                          <CommentsSection comments={question?.dataAnalise} />
                        </Box>
                      )}
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Modal>
  );
}
